// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/WorkspaceCommandCard.tsx

import type {
  CommunityNavigatorNextAction,
  CommunityNavigatorNextActionCode,
  NavigatorCaseWorkflow,
} from "../types";

type WorkspaceCommandCardProps = {
  workflow: NavigatorCaseWorkflow | null;
  nextAction: CommunityNavigatorNextAction | null;
  isRunningMatch: boolean;
  isClosingCase: boolean;
  notice: string;
  canAssignToMe: boolean;
  isAssigningCase: boolean;
  assignError: string | null;
  onAssignToMe: () => void;
  onCompleteIntake: () => void;
  onFindMatches: () => void;
  onForwardToProvider: () => void;
  onCloseCase: () => void;
};

type WorkspaceActionKey =
  | "assign"
  | "intake"
  | "match"
  | "forward"
  | "close";

function getPrimaryWorkspaceAction(
  nextActionCode: CommunityNavigatorNextActionCode | null | undefined
): WorkspaceActionKey | null {
  switch (nextActionCode) {
    case "AssignCase":
      return "assign";
    case "CompleteIntake":
      return "intake";
    case "RunMatching":
    case "FindAnotherMatch":
    case "NoSuitableMatch":
      return "match";
    case "SelectProvider":
      return "forward";
    case "CloseCase":
      return "close";
    default:
      return null;
  }
}

function actionClassName(
  action: WorkspaceActionKey,
  primary: WorkspaceActionKey | null
): string {
  return [
    "cn-case-detail-command-action",
    primary === action
      ? "cn-case-detail-command-action-primary"
      : "cn-case-detail-command-action-secondary",
  ].join(" ");
}

function shouldShowWorkspaceNotice(
  workflow: NavigatorCaseWorkflow | null,
  canAssignToMe: boolean
): boolean {
  if (canAssignToMe) return true;
  if (!workflow) return true;
  if (workflow.isClosed) return true;
  if (workflow.isForwarded) return true;
  if (!workflow.isAssignedToMe) return true;
  if (workflow.hasLatestMatchRun && !workflow.hasEligibleMatchResults) {
    return true;
  }
  return false;
}

export default function WorkspaceCommandCard({
  workflow,
  nextAction,
  isRunningMatch,
  isClosingCase,
  notice,
  canAssignToMe,
  isAssigningCase,
  assignError,
  onAssignToMe,
  onCompleteIntake,
  onFindMatches,
  onForwardToProvider,
  onCloseCase,
}: WorkspaceCommandCardProps) {
  const primary = getPrimaryWorkspaceAction(nextAction?.code);
  const showNotice = shouldShowWorkspaceNotice(workflow, canAssignToMe);

  return (
    <section
      className="cn-case-detail-command-card"
      aria-label="Navigator workspace"
    >
      <div className="cn-case-detail-command-main">
        <p className="cn-case-detail-command-eyebrow">Navigator workspace</p>

        {nextAction ? (
          <div className="cn-case-detail-command-next-action">
            <span>Next action</span>
            <strong>{nextAction.label}</strong>
          </div>
        ) : null}

        {showNotice && notice ? (
          <p className="cn-case-detail-command-notice">{notice}</p>
        ) : null}

        {assignError ? (
          <p className="cn-case-detail-command-error" role="alert">
            {assignError}
          </p>
        ) : null}
      </div>

      <div className="cn-case-detail-command-actions">
        {canAssignToMe ? (
          <button
            type="button"
            className={actionClassName("assign", primary)}
            disabled={isAssigningCase}
            onClick={onAssignToMe}
          >
            {isAssigningCase ? "Assigning…" : "Assign to me and start"}
          </button>
        ) : null}

        <button
          type="button"
          className={actionClassName("intake", primary)}
          disabled={!workflow?.canEditIntake}
          onClick={onCompleteIntake}
        >
          Complete intake
        </button>

        <button
          type="button"
          className={actionClassName("match", primary)}
          disabled={!workflow?.canRunMatching || isRunningMatch}
          onClick={onFindMatches}
        >
          {isRunningMatch ? "Finding matches..." : "Find matches"}
        </button>

        <button
          type="button"
          className={actionClassName("forward", primary)}
          disabled={!workflow?.canForwardToProvider}
          onClick={onForwardToProvider}
        >
          Forward to provider
        </button>

        {workflow?.canCloseCase === true ? (
          <button
            type="button"
            className={actionClassName("close", primary)}
            disabled={isClosingCase}
            onClick={onCloseCase}
          >
            {isClosingCase ? "Closing..." : "Close case"}
          </button>
        ) : null}
      </div>
    </section>
  );
}
